import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { formatCurrency, formatDate, toISODateString } from '../lib/utils';
import { Plus, Pencil, Trash2, CalendarIcon, TrendingUp, TrendingDown, Minus, Users } from 'lucide-react';
import { format, getISOWeek, startOfISOWeek, endOfISOWeek, setISOWeek, setYear } from 'date-fns';
import { fr } from 'date-fns/locale';
import { MissingDatesWarning } from '../components/MissingDatesWarning';

function getStatusBadge(ecart) {
  if (ecart > 0) return <Badge variant="destructive" className="gap-1"><TrendingUp className="w-3 h-3" /> Dépassement</Badge>;
  if (ecart < 0) return <Badge className="gap-1 bg-blue-100 text-blue-800 hover:bg-blue-100"><TrendingDown className="w-3 h-3" /> Écourté</Badge>;
  return <Badge variant="outline" className="gap-1"><Minus className="w-3 h-3" /> Conforme</Badge>;
}

export function SynthesePlanningPage() {
  const { api } = useAuth();
  const [data, setData] = useState([]);
  const [absencesSemaine, setAbsencesSemaine] = useState([]);
  const [allEmployes, setAllEmployes] = useState([]);
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [availableEmployes, setAvailableEmployes] = useState([]);
  const [semaine, setSemaine] = useState(getISOWeek(new Date()));
  const [filterEmploye, setFilterEmploye] = useState('all');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [prefillDate, setPrefillDate] = useState(null);

  const fetchMeta = useCallback(async () => {
    try {
      const res = await api.get('/synthese-planning/meta');
      setAvailableWeeks(res.data.weeks || []);
      setAvailableEmployes(res.data.employes || []);
    } catch {}
  }, [api]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (semaine) params.semaine = semaine;
      if (filterEmploye !== 'all') params.employe = filterEmploye;
      const res = await api.get('/synthese-planning', { params });
      setData(res.data);
      // Charger les absences (filtrées côté front par la semaine)
      try {
        const absRes = await api.get('/rh/absences');
        setAbsencesSemaine(absRes.data || []);
      } catch { setAbsencesSemaine([]); }
    } catch { toast.error('Erreur chargement'); }
    setLoading(false);
  }, [api, semaine, filterEmploye]);

  const fetchEmployes = useCallback(async () => {
    try {
      // Source 1 : module RH (employés actifs avec poste) — source de vérité préférée
      const rhRes = await api.get('/rh/employes').catch(() => ({ data: [] }));
      const rhNames = (rhRes.data || [])
        .filter(e => e.actif !== false)
        .map(e => (e.prenom || '').trim())
        .filter(Boolean);
      // Source 2 : référentiel listes/employe (legacy)
      const listeRes = await api.get('/listes/employe').catch(() => ({ data: [] }));
      const listeNames = (listeRes.data || []).map(e => e.valeur).filter(Boolean);
      // Normalisation pour dédup intelligente (lowercase, sans accents)
      const normalize = (s) => (s || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const seen = new Map();
      // 1. RH d'abord (gagne en priorité, ex: "Léna" l'emporte sur "LENA")
      rhNames.forEach(n => {
        const k = normalize(n);
        if (k && !seen.has(k)) seen.set(k, n);
      });
      // 2. Référentiel ensuite (ajoute uniquement les noms qui n'existent pas déjà)
      listeNames.forEach(n => {
        const k = normalize(n);
        if (k && !seen.has(k)) seen.set(k, n);
      });
      setAllEmployes([...seen.values()].sort((a, b) => a.localeCompare(b, 'fr')));
    } catch { setAllEmployes([]); }
  }, [api]);

  useEffect(() => { fetchMeta(); fetchEmployes(); }, [fetchMeta, fetchEmployes]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async (form, itemId) => {
    try {
      if (itemId) {
        await api.put(`/synthese-planning/${itemId}`, form);
        toast.success('Modifié');
      } else {
        await api.post('/synthese-planning', form);
        toast.success('Créé');
      }
      setDialogOpen(false);
      setEditItem(null);
      fetchData();
      fetchMeta();
    } catch { toast.error('Erreur'); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/synthese-planning/${id}`);
      toast.success('Supprimé');
      fetchData();
      fetchMeta();
    } catch { toast.error('Erreur'); }
  };

  // Totals
  const calcPause = (h) => Math.floor(h / 6) * (20 / 60);
  const totalReelles = data.reduce((s, i) => s + i.heures_reelles, 0);
  const totalPrevues = data.reduce((s, i) => s + i.heures_prevues, 0);
  const totalEcartEuros = data.reduce((s, i) => s + i.ecart_euros, 0);
  const totalPause = data.reduce((s, i) => s + calcPause(i.heures_reelles), 0);

  // Per-employee breakdown
  const byEmploye = {};
  data.forEach(i => {
    if (!byEmploye[i.employe]) byEmploye[i.employe] = { prevues: 0, reelles: 0, ecart_euros: 0, count: 0, pause: 0 };
    byEmploye[i.employe].prevues += i.heures_prevues;
    byEmploye[i.employe].reelles += i.heures_reelles;
    byEmploye[i.employe].ecart_euros += i.ecart_euros;
    byEmploye[i.employe].count += 1;
    byEmploye[i.employe].pause += calcPause(i.heures_reelles);
  });

  // Weeks to show in dropdown: merge available + current
  const currentWeek = getISOWeek(new Date());
  const weeksToShow = [...new Set([...availableWeeks, currentWeek])].sort((a, b) => a - b);

  return (
    <div className="space-y-6">
      <div className="module-header">
        <div>
          <h1 className="module-title" data-testid="synthese-title">Synthèse Planning</h1>
          <p className="text-muted-foreground mt-1">Heures réelles vs prévues et écarts</p>
        </div>
        <Button className="gap-2" onClick={() => { setEditItem(null); setDialogOpen(true); }} data-testid="btn-add-synthese">
          <Plus className="w-4 h-4" /> Saisir heures réelles
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Label className="text-sm whitespace-nowrap">Semaine :</Label>
          <Select value={semaine?.toString() || 'all'} onValueChange={(v) => setSemaine(v === 'all' ? null : parseInt(v))}>
            <SelectTrigger className="w-[180px]" data-testid="filter-semaine">
              <SelectValue placeholder="Toutes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les semaines</SelectItem>
              {weeksToShow.map(s => (
                <SelectItem key={s} value={s.toString()}>
                  Semaine {s} {s === currentWeek ? '(actuelle)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-sm whitespace-nowrap">Employé :</Label>
          <Select value={filterEmploye} onValueChange={setFilterEmploye}>
            <SelectTrigger className="w-[180px]" data-testid="filter-employe">
              <SelectValue placeholder="Tous" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les employés</SelectItem>
              {availableEmployes.map(e => (
                <SelectItem key={e} value={e}>{e}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {(semaine || filterEmploye !== 'all') && (
          <Button variant="ghost" size="sm" onClick={() => { setSemaine(null); setFilterEmploye('all'); }}>
            Réinitialiser
          </Button>
        )}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Heures Prévues</CardTitle></CardHeader>
          <CardContent><p className="text-3xl font-bold tabular-nums">{totalPrevues.toFixed(1)}h</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Heures Réelles</CardTitle></CardHeader>
          <CardContent><p className="text-3xl font-bold tabular-nums">{totalReelles.toFixed(1)}h</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Pauses (20min/6h)</CardTitle></CardHeader>
          <CardContent><p className="text-3xl font-bold tabular-nums text-orange-600">{totalPause.toFixed(1)}h</p><p className="text-xs text-muted-foreground">{Math.round(totalPause * 60)} min</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Écart Heures</CardTitle></CardHeader>
          <CardContent>
            <p className={`text-3xl font-bold tabular-nums ${(totalReelles - totalPrevues) > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {(totalReelles - totalPrevues) > 0 ? '+' : ''}{(totalReelles - totalPrevues).toFixed(1)}h
            </p>
          </CardContent>
        </Card>
        <Card className={totalEcartEuros > 0 ? 'border-red-300 bg-red-50/50 dark:bg-red-900/10' : ''}>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Écart Coût</CardTitle></CardHeader>
          <CardContent>
            <p className={`text-3xl font-bold tabular-nums ${totalEcartEuros > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {totalEcartEuros > 0 ? '+' : ''}{formatCurrency(totalEcartEuros)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Per-employee breakdown */}
      {Object.keys(byEmploye).length > 1 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4" /> Résumé par employé</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employé</TableHead>
                  <TableHead className="text-right">Jours</TableHead>
                  <TableHead className="text-right">H. Prévues</TableHead>
                  <TableHead className="text-right">H. Réelles</TableHead>
                  <TableHead className="text-right">Pause</TableHead>
                  <TableHead className="text-right">Écart H.</TableHead>
                  <TableHead className="text-right">Écart Coût</TableHead>
                  <TableHead>Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(byEmploye).sort(([a], [b]) => a.localeCompare(b)).map(([emp, totals]) => {
                  const ecartH = totals.reelles - totals.prevues;
                  return (
                    <TableRow key={emp} className="cursor-pointer hover:bg-muted/50" onClick={() => setFilterEmploye(emp)}>
                      <TableCell className="font-medium">{emp}</TableCell>
                      <TableCell className="text-right tabular-nums">{totals.count}</TableCell>
                      <TableCell className="text-right tabular-nums">{totals.prevues.toFixed(1)}h</TableCell>
                      <TableCell className="text-right tabular-nums">{totals.reelles.toFixed(1)}h</TableCell>
                      <TableCell className="text-right tabular-nums text-orange-600">{totals.pause.toFixed(1)}h <span className="text-xs text-muted-foreground">({Math.round(totals.pause * 60)}min)</span></TableCell>
                      <TableCell className={`text-right tabular-nums ${ecartH > 0 ? 'text-red-600' : ecartH < 0 ? 'text-blue-600' : ''}`}>
                        {ecartH > 0 ? '+' : ''}{ecartH.toFixed(1)}h
                      </TableCell>
                      <TableCell className={`text-right tabular-nums font-medium ${totals.ecart_euros > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {totals.ecart_euros > 0 ? '+' : ''}{formatCurrency(totals.ecart_euros)}
                      </TableCell>
                      <TableCell>{getStatusBadge(totals.ecart_euros)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Missing dates warning for selected week */}
      {semaine && (() => {
        const refDate = setISOWeek(new Date(), semaine);
        const weekStart = toISODateString(startOfISOWeek(refDate));
        const weekEnd = toISODateString(endOfISOWeek(refDate));
        // Absences chevauchant la semaine
        const absencesActives = absencesSemaine.filter(a => a.date_debut <= weekEnd && a.date_fin >= weekStart);
        return (
          <>
            <MissingDatesWarning
              data={data}
              dateField="date"
              dateFrom={weekStart}
              dateTo={weekEnd}
              onDateClick={(date) => { setPrefillDate(date); setEditItem(null); setDialogOpen(true); }}
            />
            {absencesActives.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="absences-banner">
                <p className="text-sm font-semibold text-amber-900 mb-1.5">Absences sur cette semaine ({absencesActives.length})</p>
                <div className="flex flex-wrap gap-2">
                  {absencesActives.map(a => (
                    <span key={a.id} className="text-xs px-2 py-1 rounded-full bg-white border border-amber-300 text-amber-800">
                      <b>{a.employe_nom}</b> · {a.motif} · {a.date_debut} → {a.date_fin}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        );
      })()}

      {/* Detail table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Détail ({data.length} ligne{data.length > 1 ? 's' : ''})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Semaine</TableHead>
                <TableHead>Employé</TableHead>
                <TableHead className="text-right">H. Prévues</TableHead>
                <TableHead className="text-right">H. Réelles</TableHead>
                <TableHead className="text-right">Pause</TableHead>
                <TableHead className="text-right">Écart H.</TableHead>
                <TableHead className="text-right">Coût/H</TableHead>
                <TableHead className="text-right">Écart Coût</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="w-[80px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                    {loading ? 'Chargement...' : 'Aucune synthèse pour cette sélection'}
                  </TableCell>
                </TableRow>
              ) : data.map(item => (
                <TableRow key={item.id}>
                  <TableCell>{formatDate(item.date)}</TableCell>
                  <TableCell><Badge variant="secondary" className="text-xs">S{item.semaine}</Badge></TableCell>
                  <TableCell className="font-medium">{item.employe}</TableCell>
                  <TableCell className="text-right tabular-nums">{item.heures_prevues.toFixed(1)}h</TableCell>
                  <TableCell className="text-right tabular-nums">{item.heures_reelles.toFixed(1)}h</TableCell>
                  <TableCell className="text-right tabular-nums text-orange-600">{calcPause(item.heures_reelles) > 0 ? `${Math.round(calcPause(item.heures_reelles) * 60)}min` : '-'}</TableCell>
                  <TableCell className={`text-right tabular-nums ${item.ecart_heures > 0 ? 'text-red-600' : item.ecart_heures < 0 ? 'text-blue-600' : ''}`}>
                    {item.ecart_heures > 0 ? '+' : ''}{item.ecart_heures.toFixed(1)}h
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{formatCurrency(item.cout_horaire)}</TableCell>
                  <TableCell className={`text-right tabular-nums font-medium ${item.ecart_euros > 0 ? 'text-red-600' : item.ecart_euros < 0 ? 'text-green-600' : ''}`}>
                    {item.ecart_euros > 0 ? '+' : ''}{formatCurrency(item.ecart_euros)}
                  </TableCell>
                  <TableCell>{getStatusBadge(item.ecart_euros)}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditItem(item); setDialogOpen(true); }}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Supprimer ?</AlertDialogTitle><AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(item.id)}>Supprimer</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Form dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editItem ? 'Modifier' : 'Saisir'} Heures Réelles</DialogTitle>
          </DialogHeader>
          <SyntheseForm item={editItem} employes={allEmployes.length > 0 ? allEmployes : availableEmployes} onClose={() => { setDialogOpen(false); setPrefillDate(null); }} onSave={handleSave} defaultDate={prefillDate} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SyntheseForm({ item, employes, onClose, onSave, defaultDate }) {
  const [form, setForm] = useState(item ? {
    date: item.date, employe: item.employe,
    heures_reelles: item.heures_reelles, cout_horaire: item.cout_horaire
  } : {
    date: defaultDate || toISODateString(new Date()), employe: employes[0] || '',
    heures_reelles: 0, cout_horaire: 15
  });
  const [dateOpen, setDateOpen] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.employe) { toast.error('Sélectionnez un employé'); return; }
    onSave(form, item?.id);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Date</Label>
          <Popover open={dateOpen} onOpenChange={setDateOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="synthese-date-btn">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {form.date ? format(new Date(form.date + 'T12:00:00'), 'PPP', { locale: fr }) : 'Choisir'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar mode="single" selected={form.date ? new Date(form.date + 'T12:00:00') : undefined}
                onSelect={(date) => { if (date) { setForm({ ...form, date: toISODateString(date) }); setDateOpen(false); } }} locale={fr} />
            </PopoverContent>
          </Popover>
        </div>
        <div className="space-y-2">
          <Label>Employé</Label>
          <Select value={form.employe} onValueChange={(v) => setForm({ ...form, employe: v })}>
            <SelectTrigger data-testid="synthese-employe"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
            <SelectContent>
              {employes.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Heures réelles</Label>
          <Input type="number" step="0.5" value={form.heures_reelles}
            onChange={(e) => setForm({ ...form, heures_reelles: parseFloat(e.target.value) || 0 })} data-testid="synthese-heures" />
        </div>
        <div className="space-y-2">
          <Label>Coût horaire (EUR)</Label>
          <Input type="number" step="0.5" value={form.cout_horaire}
            onChange={(e) => setForm({ ...form, cout_horaire: parseFloat(e.target.value) || 0 })} data-testid="synthese-cout" />
        </div>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
        <Button type="submit" data-testid="synthese-submit">{item ? 'Modifier' : 'Créer'}</Button>
      </DialogFooter>
    </form>
  );
}
